import { useEffect, useState } from 'react';
import GeneralModal from '../../../component/modal/GeneralModal';
import { Trans, useTranslation } from 'react-i18next';
import {
  EmailSent,
  STEPS,
  VenueBranch,
  VenueSetupData,
  VenueStep,
} from './type';
import VenueDetails from './venuDetails';
import UniversalStaffLogin from './universalStaffLogin';
import ReviewDetails from './reviewDetails';
import { StorageKey } from '../../../utils/helper.type';
import { UserService } from '../../../service/userService';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { route } from '../../../service/router';
import { AuthService } from '../../../service/authService';
import ConfirmationModal from '../../../component/confirmationModal/confirmationModal';

const VenueSetup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<VenueStep>(
    STEPS.VENUE_DETAILS,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmationModalOnSkip, setShowConfirmationModalOnSkip] =
    useState(false);
  const [showConfirmationModalOnContinue, setShowConfirmationModalOnContinue] =
    useState(false);
  const [continueUniversalLoginDisabled, setContinueUniversalLoginDisabled] =
    useState(true);
  const [currentBranchIndex, setCurrentBranchIndex] = useState(0);
  const [emailSent, setEmailSent] = useState<EmailSent>({});
  const [continueVenueDetailsDisabled, setContinueVenueDetailsDisabled] =
    useState(true);
  const [venueData, setVenueData] = useState<VenueSetupData>({
    venue: { id: '', name: '' },
    venueBranches: [],
  });
  const STORAGE_KEY = StorageKey.VENUE_UNIT_DETAILS;
  const updateStep = (step: VenueStep) => setCurrentStep(step);
  const nextStep = () => updateStep((currentStep + 1) as VenueStep);
  const prevStep = () => {
    if (currentStep === STEPS.STAFF_LOGIN && currentBranchIndex > 0) {
      setCurrentBranchIndex(currentBranchIndex - 1);
    } else {
      updateStep((currentStep - 1) as VenueStep);
    }
  };

  const validateCurrentStep = () => {
    if (currentStep === STEPS.VENUE_DETAILS) {
      // Validate venue name
      if (!venueData?.venue?.name?.trim()) {
        return false;
      }
      // Validate all branch names
      return venueData?.venueBranches?.every(
        (branch) => branch?.name?.trim() !== '',
      );
    }
    return true;
  };

  const loadVenueDetails = () => {
    try {
      const venueUnitDetails = localStorage.getItem(STORAGE_KEY);
      if (!venueUnitDetails) return;

      const { venue, venueBranches } = JSON.parse(venueUnitDetails);
      if (!venue?.name) return;

      // Update venue name
      handleChange('venue', venue);

      // Update venue branches that need universal login setup
      if (Array.isArray(venueBranches)) {
        const branchesNeedingSetup: VenueBranch[] = venueBranches
          .filter((branch) => !branch.hasUniversalLogin)
          .map(({ id, name, hasUniversalLogin }) => ({
            id,
            name,
            hasUniversalLogin,
            email: '',
            password: '',
            verificationCode: '',
            confirmPassword: '',
          }));

        if (branchesNeedingSetup.length > 0) {
          handleChange('venueBranches', branchesNeedingSetup);
        }
      }
    } catch (error) {
      console.error('Error loading venue details:', error);
    }
  };

  const handleContinue = async () => {
    if (!validateCurrentStep()) {
      return;
    }
    if (currentStep === STEPS.VENUE_DETAILS) {
      nextStep();
    } else if (currentStep === STEPS.STAFF_LOGIN) {
      // Check if any verification codes have been sent for other emails
      const hasOtherVerifiedEmails =
        emailSent[venueData?.venueBranches?.[currentBranchIndex]?.id]?.length >
        1;
      if (hasOtherVerifiedEmails) {
        setShowConfirmationModalOnContinue(true);
      } else {
        // No emails verified for other emails, just move to next branch
        const isLastBranch =
          currentBranchIndex === venueData?.venueBranches?.length - 1;
        if (!isLastBranch) {
          setCurrentBranchIndex(currentBranchIndex + 1);
          setContinueUniversalLoginDisabled(true);
        } else {
          setCurrentStep(STEPS.REVIEW);
        }
      }
    } else if (currentStep === STEPS.REVIEW) {
      setIsLoading(true);
      try {
        // Add your submission logic here
      } catch (error) {
        console.error('Error submitting venue setup:', error);
      } finally {
        setIsLoading(false);
      }
      return;
    }
  };

  const handleDeleteUser = async () => {
    setIsLoading(true);
    try {
      const userService = new UserService();
      const currentBranch = venueData?.venueBranches?.[currentBranchIndex];

      // Get all emails that need to be deleted (all emails that have been sent verification codes)
      const emailsToDelete = emailSent[currentBranch?.id]?.length;

      if (emailsToDelete === 0) {
        toast.error(t('venueSetup.verificationCodeNotSent'));
        return;
      }

      // Delete users based on confirmation modal state
      if (showConfirmationModalOnContinue) {
        // Delete all users except current branch email
        const emailsToDelete = emailSent[currentBranch?.id]
          ?.filter((item) => item.email !== currentBranch?.email)
          ?.map((item) => item.userId);
        await userService.deleteUser(emailsToDelete);
        if (emailsToDelete.length > 1) {
          toast.success(t('universalStaffLoginModal.usersDeleted'));
        } else {
          toast.success(t('universalStaffLoginModal.userDeleted'));
        }
      } else {
        // Delete all users
        const emailsToDelete = emailSent[currentBranch?.id]?.map(
          (item) => item.userId,
        );
        await userService.deleteUser(emailsToDelete);
        if (emailsToDelete.length > 1) {
          toast.success(t('universalStaffLoginModal.usersDeleted'));
        } else {
          toast.success(t('universalStaffLoginModal.userDeleted'));
        }
      }

      // Clear email sent state for all deleted users
      // Clear email sent state for current branch
      setEmailSent((prev) => {
        const updated = { ...prev };
        if (showConfirmationModalOnContinue) {
          // Keep only the current branch email if it exists
          updated[currentBranch?.id] = updated[currentBranch?.id]?.filter(
            (item) =>
              item.email ===
              venueData?.venueBranches?.[currentBranchIndex]?.email,
          );
          if (!updated[currentBranch?.id]?.length) {
            delete updated[currentBranch?.id];
          }
        } else {
          // Delete all emails for current branch
          delete updated[currentBranch?.id];
        }
        return updated;
      });

      if (showConfirmationModalOnSkip) {
        // Clear email and password for current branch
        setVenueData((prev) => ({
          ...prev,
          venueBranches: prev?.venueBranches?.map((branch) =>
            branch?.id === currentBranch?.id
              ? {
                  ...branch,
                  email: '',
                  password: '',
                  verificationCode: '',
                  confirmPassword: '',
                }
              : branch,
          ),
        }));
      }
      setShowConfirmationModalOnSkip(false);
      setShowConfirmationModalOnContinue(false);
      const isLastBranch =
        currentBranchIndex >= venueData?.venueBranches?.length - 1;
      if (isLastBranch) {
        nextStep();
      } else {
        setCurrentBranchIndex(currentBranchIndex + 1);
        setContinueUniversalLoginDisabled(true);
      }
    } catch (error) {
      console.error('Error deleting users:', error);
      toast.error(t('venueSetup.errorDeletingUser'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    if (currentStep === STEPS.REVIEW) return;

    if (currentStep === STEPS.STAFF_LOGIN) {
      // Check if any verification codes have been sent
      const hasVerifiedEmails =
        emailSent[venueData?.venueBranches?.[currentBranchIndex]?.id]?.length;

      if (hasVerifiedEmails) {
        setShowConfirmationModalOnSkip(true);
      } else {
        // No emails verified, just move to next branch
        const isLastBranch =
          currentBranchIndex >= venueData?.venueBranches?.length - 1;
        if (isLastBranch) {
          nextStep();
        } else {
          setCurrentBranchIndex(currentBranchIndex + 1);
          setContinueUniversalLoginDisabled(true);
        }
      }
    } else if (currentStep === STEPS.VENUE_DETAILS) {
      loadVenueDetails();
      nextStep();
    }
  };

  const handleChange = (field: keyof VenueSetupData, value: any) => {
    setVenueData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEmailChange = (branchId: string, value: string) => {
    if (emailSent[branchId]?.find((item) => item.email !== value)) {
      setVenueData((prev) => ({
        ...prev,
        venueBranches: prev?.venueBranches?.map((branch) =>
          branch?.id === branchId
            ? {
                ...branch,
                verificationCode: '',
                password: '',
                confirmPassword: '',
              }
            : branch,
        ),
      }));
      setContinueUniversalLoginDisabled(true);
      return;
    }
  };

  const handleBranchChange = (
    branchId: string,
    field:
      | 'name'
      | 'email'
      | 'password'
      | 'verificationCode'
      | 'confirmPassword',
    value: string,
  ) => {
    setVenueData((prev) => ({
      ...prev,
      venueBranches: prev?.venueBranches?.map((branch) =>
        branch?.id === branchId ? { ...branch, [field]: value } : branch,
      ),
    }));
  };

  const updateEmailSent = (branchId: string, email: string, userId: string) => {
    setEmailSent((prev) => ({
      ...prev,
      [branchId]: [...(prev[branchId] || []), { email, userId }],
    }));
  };

  const getStepContent = () => {
    switch (currentStep) {
      case STEPS.VENUE_DETAILS:
        return (
          <VenueDetails
            venueData={venueData}
            handleChange={handleChange}
            handleBranchChange={handleBranchChange}
            enableContinueButton={() => setContinueVenueDetailsDisabled(false)}
          />
        );
      case STEPS.STAFF_LOGIN:
        return (
          <UniversalStaffLogin
            branch={venueData?.venueBranches?.[currentBranchIndex]}
            handleBranchChange={handleBranchChange}
            enableContinueButton={() =>
              setContinueUniversalLoginDisabled(false)
            }
            venueId={venueData?.venue?.id}
            emailsSent={
              emailSent?.[venueData?.venueBranches?.[currentBranchIndex]?.id]
            }
            updateEmailSent={updateEmailSent}
            handleEmailChange={handleEmailChange}
          />
        );
      case STEPS.REVIEW:
        return <ReviewDetails venueData={venueData} />;
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    const titles = {
      [STEPS.VENUE_DETAILS]: t('venueSetup.venueSetupTitle'),
      [STEPS.STAFF_LOGIN]: t('venueSetup.universalStaffLoginTitle'),
      [STEPS.REVIEW]: t('venueSetup.reviewDetailsTitle'),
    };
    return titles[currentStep];
  };

  const getStepDescription = () => {
    const descriptions = {
      [STEPS.VENUE_DETAILS]: t('venueSetup.venueSetupDescription'),
      [STEPS.STAFF_LOGIN]: t('venueSetup.universalStaffLoginDescription'),
      [STEPS.REVIEW]: t('venueSetup.reviewDetailsDescription'),
    };
    return descriptions[currentStep];
  };

  const handleFinish = async () => {
    try {
      // Get original venue details from localStorage
      const venueUnitDetails = localStorage.getItem(STORAGE_KEY);
      if (!venueUnitDetails) return;

      let originalData: VenueSetupData = JSON.parse(venueUnitDetails);
      let editVenuePromises: Promise<any>[] = [];
      const userService = new UserService();
      const authService = new AuthService();

      // Check if venue name has changed
      if (originalData?.venue?.name !== venueData?.venue?.name) {
        editVenuePromises.push(
          userService.editVenue(venueData?.venue?.id, venueData?.venue?.name),
        );
      }

      // Check if any branch names have changed
      venueData?.venueBranches?.forEach((branch) => {
        const originalBranch = originalData?.venueBranches?.find(
          (b: any) => b?.id === branch?.id,
        );
        if (originalBranch && originalBranch?.name !== branch?.name) {
          editVenuePromises.push(
            userService.editVenue(branch?.id, branch?.name),
          );
        }
      });

      // Check if any universal staff logins were set up
      const hasUniversalStaffLogins: boolean = venueData?.venueBranches?.some(
        (branch: VenueBranch) => branch?.email && branch?.password,
      );

      // Only show loading and make API calls if there are changes
      if (editVenuePromises.length > 0 || hasUniversalStaffLogins) {
        setIsLoading(true);

        if (editVenuePromises.length > 0) {
          await Promise.all(editVenuePromises);
          toast.success('Venue details updated successfully');
        }

        if (hasUniversalStaffLogins) {
          const branchesWithLogins = venueData?.venueBranches?.filter(
            (branch: VenueBranch) => branch?.email && branch?.password,
          );

          await Promise.all(
            branchesWithLogins?.map((branch: VenueBranch) =>
              authService.newPassword(
                branch?.email,
                branch?.password,
                branch?.verificationCode,
              ),
            ),
          );
          toast.success('Universal staff login setup successfully');
        }
        navigate(route.dashboard);
      } else {
        // No changes were made, just navigate to dashboard
        navigate(route.dashboard);
      }
    } catch (error) {
      console.error('Error updating venue details:', error);
      toast.error('Failed to update venue details');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadVenueDetails();
  }, []);

  return (
    <>
      <GeneralModal
        continueButtonDisabled={
          currentStep === STEPS.VENUE_DETAILS
            ? continueVenueDetailsDisabled
            : continueUniversalLoginDisabled
        }
        onContinue={currentStep !== STEPS.REVIEW ? handleContinue : undefined}
        onGoBack={prevStep}
        headerTitleStyle="text-start font-inter font-semibold leading-5"
        onSkip={currentStep !== STEPS.REVIEW ? handleSkip : undefined}
        headerTitle={getStepTitle()}
        showBackButton={currentStep !== STEPS.VENUE_DETAILS}
        body={
          <div className="flex flex-col items-start gap-2 px-0 w-full max-w-2xl">
            {currentStep !== STEPS.REVIEW && (
              <div className="text-l font-semibold mb-2">
                {currentStep === STEPS.VENUE_DETAILS
                  ? t('venueSetup.details')
                  : t(
                      `Set Universal Staff Login for ${venueData?.venue?.name} ${venueData?.venueBranches?.[currentBranchIndex]?.name}`,
                    )}
              </div>
            )}
            <div className="text-black mb-6 max-w-2xl text-xs font-normal">
              {getStepDescription()}
            </div>
            {getStepContent()}
          </div>
        }
        continueText={
          currentStep !== STEPS.REVIEW ? t('venueSetup.continue') : undefined
        }
        skipText={
          currentStep !== STEPS.REVIEW ? t('venueSetup.skip') : undefined
        }
        finishText={
          currentStep === STEPS.REVIEW ? t('venueSetup.finish') : undefined
        }
        onFinish={currentStep === STEPS.REVIEW ? handleFinish : undefined}
        isLoading={isLoading}
      />

      {(showConfirmationModalOnSkip || showConfirmationModalOnContinue) && (
        <ConfirmationModal
          isLoading={isLoading}
          showConfirmButton
          isOpen={
            showConfirmationModalOnSkip || showConfirmationModalOnContinue
          }
          onConfirm={handleDeleteUser}
          onClose={() =>
            showConfirmationModalOnSkip
              ? setShowConfirmationModalOnSkip(false)
              : setShowConfirmationModalOnContinue(false)
          }
          message={
            <Trans
              i18nKey={
                showConfirmationModalOnSkip
                  ? 'venueSetup.confirmMessageOnSkip'
                  : (() => {
                      const extraEmails =
                        emailSent[
                          venueData?.venueBranches?.[currentBranchIndex]?.id
                        ]?.filter(
                          (item) =>
                            item.email !==
                            venueData?.venueBranches?.[currentBranchIndex]
                              ?.email,
                        ) || [];
                      return extraEmails.length === 1
                        ? 'venueSetup.continueMessageSingle'
                        : 'venueSetup.continueMessageMultiple';
                    })()
              }
              values={
                showConfirmationModalOnContinue && {
                  userText: emailSent[
                    venueData?.venueBranches?.[currentBranchIndex]?.id
                  ]
                    ?.filter(
                      (item) =>
                        item.email !==
                        venueData?.venueBranches?.[currentBranchIndex]?.email,
                    )
                    ?.map((item) => item.email)
                    .join(', '),
                }
              }
            />
          }
        />
      )}
    </>
  );
};

export default VenueSetup;
